import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getDraft, retranslateDraft, updateDraft } from "@/api/drafts";
import type {
  EnforcementEvent,
  NerEntity,
  RiskPayload,
  TerminologyConstraint,
  TranslateEditorResponse,
  TranslationDraft,
} from "@/api/types";
import { useToast } from "@/components/toast";
import {
  quantityToLinkId,
  renderHighlighted,
} from "@/components/highlightedText";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useTextSelection } from "@/hooks/useTextSelection";
import { pretranslate } from "@/api/pretranslate";
import BackendSelect from "./components/BackendSelect";
import { FeedbackPopover } from "./components/FeedbackPopover";
import { DraftFeedbackPanel } from "./components/DraftFeedbackPanel";
import { currentDraftTitle, draftTitle } from "./draftTitle";

const quantityNorm = (s: string): string =>
  quantityToLinkId(s).replace(/^q-/, "");

interface LexiconMatchRaw {
  term?: string;
  domain?: string;
  source?: string;
  token_span?: [number, number];
}

interface DomainEvidence {
  evidence?: { lexicon_matches?: LexiconMatchRaw[] };
}

interface SnapshotShape {
  terminology?: { constraints?: TerminologyConstraint[] };
  risk?: RiskPayload;
  /** Risk-fragments из target_text (F8). Подсвечивается на target-pane
   *  параллельно с source-side risk. */
  target_risk?: {
    status?: string;
    high_risk_fragments?: RiskPayload["high_risk_fragments"];
    summary?: { high_risk_fragment_count?: number };
  };
  translation?: TranslateEditorResponse["translation"];
  decision_summary?: TranslateEditorResponse["decision_summary"];
  review?: TranslateEditorResponse["review"];
  domain?: DomainEvidence;
  /** NER entities из source text — drug/dose/person/section. */
  entities?: NerEntity[];
  // debug-view shape additions
  enforcement?: { events?: EnforcementEvent[]; status?: string };
  quality?: {
    checks?: Array<Record<string, unknown>>;
    status?: string;
    gate?: { status?: string; reason?: string };
  };
  // raw debug response when stored (legacy snapshots from earlier
  // versions of TranslateLanding) — debug nests everything under pretranslation
  pretranslation?: {
    terminology?: { constraints?: TerminologyConstraint[] };
    risk?: RiskPayload;
    domain?: DomainEvidence;
    entities?: NerEntity[];
  };
  _debug?: {
    pretranslation?: {
      domain?: DomainEvidence;
      entities?: NerEntity[];
    };
    quality?: {
      checks?: Array<Record<string, unknown>>;
    };
  };
}

export default function TranslateEditor({
  initialMode = "preview",
}: {
  initialMode?: "preview" | "edit";
} = {}) {
  const { draftId = "" } = useParams<{ draftId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [target, setTarget] = useState("");
  // Mode определяется по initialMode (по URL: /translate/:id = preview,
  // /translate/:id/edit = edit). Кнопки header'а target pane меняют URL.
  const [targetMode, setTargetMode] = useState<"preview" | "edit">(initialMode);
  // Cross-pane hover: linkId span'а под курсором → подсветка
  // одинаковых linkId в source + target + rail.
  const [activeLinkId, setActiveLinkId] = useState<string | null>(null);

  const draftQuery = useQuery({
    queryKey: ["draft", draftId],
    queryFn: async () => {
      const r = await getDraft(draftId);
      if (!r.ok) throw new Error(r.error);
      return r.data.draft;
    },
    enabled: Boolean(draftId),
  });

  // Hydrate target_text once when draft first loads.
  useEffect(() => {
    if (draftQuery.data && target === "") {
      setTarget(draftQuery.data.target_text ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftQuery.data?.draft_id]);

  const saveMutation = useMutation({
    mutationFn: async (next: {
      target_text: string;
      status?: TranslationDraft["status"];
    }) => {
      const r = await updateDraft(draftId, next);
      if (!r.ok) throw new Error(r.error);
      return r.data.draft;
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(["draft", draftId], saved);
      toast(`Сохранено · ${statusLabelRu(saved.status)}`, "success");
    },
    onError: (err: Error) => toast(`Не сохранено: ${err.message}`, "error"),
  });

  const renameMutation = useMutation({
    mutationFn: async (vars: {
      title: string;
      metadata: Record<string, unknown>;
    }) => {
      const r = await updateDraft(draftId, {
        metadata: { ...vars.metadata, title: vars.title },
      });
      if (!r.ok) throw new Error(r.error);
      return r.data.draft;
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(["draft", draftId], saved);
      toast("Название сохранено", "success");
    },
    onError: (err: Error) => toast(`Не сохранено: ${err.message}`, "error"),
  });

  // --- Editable source (Feature 5) --------------------------------------
  // Live re-pretranslate при правке source_text'а. Дороги нет: pretranslate
  // только локальная БД + regex, без вызова MT-backend'ов.
  // Защиты: debounce 700 мс, min length, react-query staleTime, enabled
  // только в edit-mode. Также под feature-flag в localStorage.
  const editableSourceEnabled =
    typeof window !== "undefined" &&
    window.localStorage?.getItem("medtranslate.editableSource") === "true";
  const [sourceMode, setSourceMode] = useState<"preview" | "edit">("preview");
  const [editedSource, setEditedSource] = useState<string>("");
  // Ленивая hydratation editedSource — чтобы не перезатирать ввод при
  // повторных draftQuery обновлениях.
  useEffect(() => {
    if (draftQuery.data && editedSource === "") {
      setEditedSource(draftQuery.data.source_text ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftQuery.data?.draft_id]);

  // --- Re-translate (Feature 3) ------------------------------------------
  // Хранит «следующий backend», который покажется в селекторе. Применение
  // происходит только по клику «Перевести заново» (и подтверждению, если
  // target_text уже отличается от исходного MT-результата).
  // Re-translate (смена движка) временно отключён: дефолтный бэкенд на dev —
  // DeepL, который отдаёт HTTP 451 (регион), и «Перевести заново» падало с
  // ошибкой. Прячем селектор движка + кнопку до решения вопроса с DeepL.
  // Поставить true — вернёт UI обратно.
  const RETRANSLATE_ENABLED: boolean = false;
  const [retranslateBackend, setRetranslateBackend] = useState<string>("");
  const [confirmRetranslate, setConfirmRetranslate] = useState<string | null>(
    null,
  );
  const retranslateMutation = useMutation({
    mutationFn: async (backend: string) => {
      const r = await retranslateDraft(draftId, { backend });
      if (!r.ok) throw new Error(r.error);
      return r.data.draft;
    },
    onSuccess: (updated) => {
      // Обновляем кэш + локальный target — иначе useEffect hydrate не
      // перепишет (он отдаёт hydrate только при первом рендере).
      queryClient.setQueryData(["draft", draftId], updated);
      setTarget(updated.target_text ?? "");
      const backend =
        (updated.metadata as Record<string, unknown> | undefined)?.[
          "selectedBackend"
        ] ?? "?";
      toast(`Переведено заново через «${backend}»`, "success");
    },
    onError: (err: Error) => toast(`Re-translate: ${err.message}`, "error"),
  });

  // ALL hooks above any early return.
  const draft = draftQuery.data ?? null;
  const snapshot: SnapshotShape =
    (draft?.pretranslate_snapshot as SnapshotShape | null | undefined) ?? {};
  // Legacy/debug snapshots may keep terminology nested under
  // `pretranslation.terminology`; new ones expose it on top.
  const constraints =
    snapshot.terminology?.constraints ??
    snapshot.pretranslation?.terminology?.constraints ??
    [];
  const risk = snapshot.risk ?? snapshot.pretranslation?.risk;
  const targetRisk = snapshot.target_risk;
  const entities: NerEntity[] =
    snapshot.entities ??
    snapshot.pretranslation?.entities ??
    snapshot._debug?.pretranslation?.entities ??
    [];
  const decision = snapshot.decision_summary;
  const review = snapshot.review;

  // Editor view stores enforcement events under review.enforcement_events;
  // debug view stores them under enforcement.events. Try both — drafts
  // saved at different times may have different shapes.
  const enforcementEvents: EnforcementEvent[] =
    review?.enforcement_events ?? snapshot.enforcement?.events ?? [];

  // Quality failed_checks similarly: editor view has review.failed_checks;
  // debug view has quality.checks (full list, need to filter status=failed).
  const failedChecks: Array<Record<string, unknown>> = useMemo(() => {
    if (review?.failed_checks && review.failed_checks.length > 0) {
      return review.failed_checks;
    }
    const all = snapshot.quality?.checks ?? [];
    return all.filter((c) => (c as { status?: string }).status === "failed");
  }, [review?.failed_checks, snapshot.quality?.checks]);

  // Domain evidence — медицинские термины контекста (info-уровень).
  // Фильтруем шум: оставляем только matches, которые либо
  //   • совпадают доменом с primary_domain (что на странице — про
  //     эндокринологию, кардиологию и т.п.),
  //   • либо точно резолвлены через mesh_descriptor (точные сопоставления),
  //   • плюс длиной от 4 символов чтобы убрать аббревиатуры/мусор.
  // Дедупликация по term — повторы все равно дают span'ы при substring search.
  const lexiconMatches = useMemo(() => {
    const domainPayload =
      snapshot.domain ??
      snapshot.pretranslation?.domain ??
      snapshot._debug?.pretranslation?.domain;
    const raw = domainPayload?.evidence?.lexicon_matches ?? [];
    const primaryDomain =
      (domainPayload as Record<string, unknown> | undefined)?.[
        "primary_domain"
      ] as string | null | undefined;
    const seen = new Set<string>();
    const out: Array<{ term: string; domain?: string; source?: string }> = [];
    for (const lm of raw) {
      if (!lm.term) continue;
      if (lm.term.length < 4) continue;
      const isMeshDescriptor = lm.source === "mesh_descriptor";
      const sameDomain =
        primaryDomain != null && lm.domain === primaryDomain;
      if (!isMeshDescriptor && !sameDomain) continue;
      if (seen.has(lm.term)) continue;
      seen.add(lm.term);
      out.push({ term: lm.term, domain: lm.domain, source: lm.source });
    }
    return out;
  }, [snapshot.domain, snapshot.pretranslation, snapshot._debug]);

  // Quality.constraints_present.details.accepted_by_variation — для target
  // pane. Достаём из quality.checks (debug shape) или review.failed_checks
  // если попадутся. На текущем backend это check_id="terminology.constraints_present".
  const acceptedVariations = useMemo(() => {
    const checks =
      snapshot.quality?.checks ??
      snapshot._debug?.quality?.checks ??
      [];
    for (const c of checks) {
      const ch = c as Record<string, unknown>;
      if (ch.check_id !== "terminology.constraints_present") continue;
      const details = (ch.details ?? {}) as Record<string, unknown>;
      const list = (details.accepted_by_variation ?? []) as Array<
        Record<string, unknown>
      >;
      return list.map((it) => ({
        span: it.span as { start: number; end: number } | undefined,
        accepted_text: it.accepted_text as string | undefined,
        matched_term: it.matched_term as string | undefined,
        target_text: it.target_text as string | undefined,
        constraint_id: it.constraint_id as string | undefined,
        match_type: it.match_type as string | undefined,
      }));
    }
    return [];
  }, [snapshot.quality?.checks, snapshot._debug?.quality?.checks]);

  // --- Live re-pretranslate query (Feature 5) ----------------------------
  // Дергаем /v1/pretranslate (нулевая стоимость — локальная БД, без MT)
  // на debounced editedSource при условии edit-mode + min length.
  // При успехе мерджим constraints/risk/entities поверх snapshot'а.
  const debouncedEditedSource = useDebouncedValue(editedSource, 700);
  const liveQuery = useQuery({
    queryKey: [
      "pretranslate-live",
      draft?.source_lang,
      draft?.target_lang,
      debouncedEditedSource,
    ],
    queryFn: async () => {
      if (!draft) throw new Error("draft not loaded");
      const r = await pretranslate({
        source_lang: draft.source_lang,
        target_lang: draft.target_lang,
        text: debouncedEditedSource,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    enabled:
      editableSourceEnabled &&
      sourceMode === "edit" &&
      !!draft &&
      debouncedEditedSource.length >= 20 &&
      debouncedEditedSource !== draft?.source_text,
    staleTime: 2000,
    refetchOnWindowFocus: false,
  });

  const liveConstraints = liveQuery.data?.terminology?.constraints;
  const liveRisk = liveQuery.data?.risk;
  const liveEntities = liveQuery.data?.entities;
  const effectiveConstraints =
    sourceMode === "edit" && liveConstraints ? liveConstraints : constraints;
  const effectiveRisk = sourceMode === "edit" && liveRisk ? liveRisk : risk;
  const effectiveEntities: NerEntity[] =
    sourceMode === "edit" && liveEntities ? liveEntities : entities;

  const saveSourceMutation = useMutation({
    mutationFn: async (next: { source_text: string }) => {
      // Сохраняем source_text + последний live-snapshot в draft.
      const snapshotPayload: Record<string, unknown> = {
        ...(liveQuery.data
          ? {
              terminology: liveQuery.data.terminology,
              risk: liveQuery.data.risk,
              domain: liveQuery.data.domain,
              entities: liveQuery.data.entities,
            }
          : {}),
        // Сохраняем translation block из старого snapshot'а — target_text
        // не пересчитывали (это явная Фича 3 «Перевести заново»).
        translation: snapshot.translation,
      };
      const r = await updateDraft(draftId, {
        source_text: next.source_text,
        pretranslate_snapshot: snapshotPayload,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data.draft;
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(["draft", draftId], saved);
      setSourceMode("preview");
      toast("Source сохранён · snapshot пересчитан", "success");
    },
    onError: (err: Error) => toast(`Не сохранено: ${err.message}`, "error"),
  });

  const activeLinkIds = useMemo(
    () =>
      activeLinkId
        ? new Set<string>([activeLinkId])
        : new Set<string>(),
    [activeLinkId],
  );

  // Делегированный hover на span'ах: читаем data-mt-link.
  const handlePaneMouseOver = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      const raw = e.target as Node | null;
      const el =
        raw && raw.nodeType === Node.ELEMENT_NODE
          ? (raw as HTMLElement)
          : (raw?.parentElement ?? null);
      const span = el?.closest<HTMLElement>("[data-mt-link]") ?? null;
      const id = span?.dataset.mtLink;
      setActiveLinkId(id ?? null);
    },
    [],
  );
  const handlePaneMouseLeave = useCallback(
    () => setActiveLinkId(null),
    [],
  );
  const sourceWords = countWords(draft?.source_text);
  const targetWords = countWords(target);

  // Feedback feature: подписка на текстовое выделение в обеих pane'ах.
  // Хук должен быть выше early-return'ов чтобы не нарушать правило hooks.
  const paneTextBySide = useMemo(
    () => ({
      source: draft?.source_text ?? "",
      target: target || draft?.target_text || "",
    }),
    [draft?.source_text, draft?.target_text, target],
  );
  const textSelection = useTextSelection(
    "[data-mt-pane-side]",
    paneTextBySide,
  );
  const [feedbackPopoverOpen, setFeedbackPopoverOpen] = useState(false);
  const [pinnedSelection, setPinnedSelection] = useState<
    null | typeof textSelection
  >(null);
  // Если popover открыт — он зафиксирован на pinnedSelection и не меняется
  // когда пользователь кликает в форму (selection в DOM может схлопнуться).
  const popoverSelection = pinnedSelection ?? textSelection;
  // Кнопка «Замечание» появляется когда есть selection и popover закрыт.
  const showCommentButton = !!textSelection && !feedbackPopoverOpen;
  function openFeedbackPopover() {
    if (!textSelection) return;
    setPinnedSelection(textSelection);
    setFeedbackPopoverOpen(true);
  }
  function closeFeedbackPopover() {
    setFeedbackPopoverOpen(false);
    setPinnedSelection(null);
  }
  const matchedConstraints = useMemo(
    () => countMatched(target, constraints),
    [target, constraints],
  );
  const missingCount = constraints.length - matchedConstraints;
  const failedCount = failedChecks.length;
  const enforcementCount = enforcementEvents.length;
  const draftIsTerminal =
    draft?.status === "sent_to_review" || draft?.status === "released";

  // Now safe to early-return.
  if (draftQuery.isLoading) {
    return (
      <div className="cl-min__centered">
        <p className="cl-min__sub">Загружаем черновик…</p>
      </div>
    );
  }
  if (draftQuery.isError || !draft) {
    return (
      <div className="cl-min__centered">
        <h2>Черновик не найден</h2>
        <p>
          <Link to="/translate" className="cl-link">
            Вернуться к переводу
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="te-shell">
      {/* DOC STRIP */}
      <header className="te-doc">
        <Link to="/translate" className="mt-btn mt-btn--sm mt-btn--ghost">
          ‹ Перевести другой
        </Link>
        <span className="te-doc__title" title={draftTitle(draft)}>
          {draftTitle(draft)}
        </span>
        <button
          className="mt-btn mt-btn--sm mt-btn--ghost"
          type="button"
          disabled={renameMutation.isPending}
          onClick={() => {
            const name = window.prompt(
              "Название черновика:",
              currentDraftTitle(draft),
            );
            if (name != null) {
              renameMutation.mutate({
                title: name.trim(),
                metadata: (draft.metadata ?? {}) as Record<string, unknown>,
              });
            }
          }}
          title="Переименовать черновик"
        >
          ✎
        </button>
        <span className="mt-text-tight mt-muted">
          {draft.source_lang.toUpperCase()} → {draft.target_lang.toUpperCase()}
        </span>
        <span className="mt-mono mt-text-tight mt-muted">
          {draft.draft_id.slice(0, 8)}
        </span>
        <div className="te-doc__meta">
          {snapshot.translation?.backend_name && (
            <span>
              движок:{" "}
              <span className="mt-mono">{snapshot.translation.backend_name}</span>
            </span>
          )}
          <span>
            статус: <span className="mt-mono">{statusLabelRu(draft.status)}</span>
          </span>
        </div>
      </header>

      {/* DECISION BANNER + STATS DOCK (top, не bottom) */}
      {decision && (
        <div className="te-banner">
          <DecisionBanner decision={decision} />
        </div>
      )}
      <div className="te-dock te-dock--top">
        <span className="te-dock__stat">
          <span>{draft.source_lang.toUpperCase()}</span>
          <strong>{sourceWords}</strong>
          <span>слов</span>
        </span>
        <span className="te-dock__stat">
          <span>{draft.target_lang.toUpperCase()}</span>
          <strong>{targetWords}</strong>
          <span>слов</span>
        </span>
        <div className="mt-divider--v" />
        <span className="te-dock__stat">
          <strong style={{ color: "var(--teal-700)" }}>{matchedConstraints}</strong>
          <span>термин{ruPluralEnd(matchedConstraints)} сопоставлен{ruPluralEndShort(matchedConstraints)}</span>
        </span>
        {missingCount > 0 && (
          <span className="te-dock__stat">
            <strong style={{ color: "var(--red-700)" }}>{missingCount}</strong>
            <span>пропущено</span>
          </span>
        )}
        {enforcementCount > 0 && (
          <>
            <div className="mt-divider--v" />
            <span className="te-dock__stat">
              <strong>{enforcementCount}</strong>
              <span>правок применено</span>
            </span>
          </>
        )}
        {failedCount > 0 && (
          <span className="te-dock__stat">
            <strong style={{ color: "var(--red-700)" }}>{failedCount}</strong>
            <span>проверк{ruPluralEnd(failedCount)} не пройдено</span>
          </span>
        )}
        <span className="te-dock__sep" />
        <span className="mt-text-tight mt-muted">
          ⌘ S — сохранить · ⌘ ⇧ Enter — отправить на ревью
        </span>
        <div className="mt-divider--v" />
        <button
          className="mt-btn mt-btn--secondary"
          disabled={saveMutation.isPending || draftIsTerminal}
          onClick={() =>
            saveMutation.mutate({ target_text: target, status: "saved" })
          }
          type="button"
        >
          {saveMutation.isPending ? "Сохранение…" : "Сохранить черновик"}
        </button>
        <button
          className="mt-btn mt-btn--primary"
          disabled={saveMutation.isPending || draftIsTerminal}
          onClick={() =>
            saveMutation.mutate({
              target_text: target,
              status: "sent_to_review",
            })
          }
          type="button"
        >
          Отправить на ревью
        </button>
      </div>

      {/* MAIN SPLIT */}
      <div className="te-main">
        {/* SOURCE */}
        <section className="te-pane">
          <div className="te-pane__head">
            <span className="te-lang">{draft.source_lang}</span>
            <span>Оригинал</span>
            <span className="mt-toolbar__sep" style={{ flex: 1 }} />
            {editableSourceEnabled && !draftIsTerminal && (
              <>
                {sourceMode === "preview" ? (
                  <button
                    type="button"
                    className="mt-btn mt-btn--sm mt-btn--ghost"
                    onClick={() => setSourceMode("edit")}
                    title="Live-аналитика обновляется на правки source-текста (F5)"
                  >
                    Править source
                  </button>
                ) : (
                  <>
                    {liveQuery.isFetching && (
                      <span className="mt-text-tight mt-muted">
                        ↻ обновление аналитики…
                      </span>
                    )}
                    <button
                      type="button"
                      className="mt-btn mt-btn--sm mt-btn--secondary"
                      disabled={
                        saveSourceMutation.isPending ||
                        editedSource === draft.source_text
                      }
                      onClick={() =>
                        saveSourceMutation.mutate({ source_text: editedSource })
                      }
                    >
                      {saveSourceMutation.isPending
                        ? "Сохранение…"
                        : "Сохранить source"}
                    </button>
                    <button
                      type="button"
                      className="mt-btn mt-btn--sm mt-btn--ghost"
                      onClick={() => {
                        setEditedSource(draft.source_text ?? "");
                        setSourceMode("preview");
                      }}
                    >
                      Отменить
                    </button>
                  </>
                )}
              </>
            )}
            <span
              className="mt-text-tight mt-muted"
              style={{ textTransform: "none", letterSpacing: 0 }}
            >
              {sourceMode === "edit"
                ? `редактирование · ${editedSource.length} симв`
                : `только просмотр · ${sourceWords} ${ruWordPlural(sourceWords)}`}
            </span>
          </div>
          <div
            className="te-pane__body"
            data-mt-pane-side="source"
            onMouseOver={handlePaneMouseOver}
            onMouseLeave={handlePaneMouseLeave}
          >
            {sourceMode === "edit" ? (
              <textarea
                className="te-pane__editable"
                value={editedSource}
                onChange={(e) => setEditedSource(e.target.value)}
                spellCheck={false}
                rows={Math.max(6, Math.ceil(editedSource.length / 80))}
                style={{ width: "100%", fontFamily: "inherit", fontSize: "inherit" }}
              />
            ) : (
              <p>
                {renderHighlighted(draft.source_text, {
                  pane: "source",
                  constraints: effectiveConstraints,
                  highRiskTerms: effectiveRisk?.high_risk_terms,
                  highRiskFragments: effectiveRisk?.high_risk_fragments,
                  attentionTerms: effectiveRisk?.attention_terms,
                  lexiconMatches,
                  entities: effectiveEntities,
                  highlightAbbreviations: true,
                  activeLinkIds,
                })}
              </p>
            )}
          </div>
        </section>

        {/* TARGET */}
        <section
          className={`te-pane te-pane--target${draftIsTerminal ? "" : " is-editing"}`}
        >
          <div className="te-pane__head">
            <span className="te-lang">{draft.target_lang}</span>
            <span>Перевод — {statusShortLabelRu(draft.status)}</span>
            <span className="mt-badge mt-badge--neutral mt-badge--no-dot">
              {statusLabelRu(draft.status)}
            </span>
            {RETRANSLATE_ENABLED && !draftIsTerminal && (
              <span
                className="te-retranslate"
                style={{ display: "inline-flex", gap: "6px", alignItems: "center" }}
              >
                <BackendSelect
                  value={
                    retranslateBackend ||
                    String(
                      (draft.metadata as Record<string, unknown> | undefined)?.[
                        "selectedBackend"
                      ] ?? "",
                    )
                  }
                  onChange={(name) => setRetranslateBackend(name)}
                  disabled={retranslateMutation.isPending}
                />
                <button
                  className="mt-btn mt-btn--sm mt-btn--secondary"
                  type="button"
                  disabled={
                    retranslateMutation.isPending ||
                    !retranslateBackend ||
                    retranslateBackend ===
                      String(
                        (draft.metadata as Record<string, unknown> | undefined)?.[
                          "selectedBackend"
                        ] ?? "",
                      )
                  }
                  onClick={() => {
                    const initialTarget = snapshot.translation?.final_text
                      ?? snapshot.translation?.translated_text
                      ?? draft.target_text
                      ?? "";
                    const hasEdits = (target ?? "") !== initialTarget;
                    if (hasEdits) {
                      setConfirmRetranslate(retranslateBackend);
                    } else {
                      retranslateMutation.mutate(retranslateBackend);
                    }
                  }}
                  title="Перезапустить перевод с другим движком (Backend select)"
                >
                  {retranslateMutation.isPending
                    ? "Переводим…"
                    : "Перевести заново"}
                </button>
              </span>
            )}
            <span className="mt-toolbar__sep" style={{ flex: 1 }} />
            <button
              className={
                "mt-btn mt-btn--sm" +
                (targetMode === "preview" ? " mt-btn--secondary" : " mt-btn--ghost")
              }
              onClick={() => {
                setTargetMode("preview");
                navigate(`/translate/${draftId}`, { replace: true });
              }}
              type="button"
              disabled={targetMode === "preview"}
            >
              Просмотр
            </button>
            <button
              className={
                "mt-btn mt-btn--sm" +
                (targetMode === "edit" ? " mt-btn--secondary" : " mt-btn--ghost")
              }
              onClick={() => {
                setTargetMode("edit");
                navigate(`/translate/${draftId}/edit`, { replace: true });
              }}
              type="button"
              disabled={draftIsTerminal || targetMode === "edit"}
            >
              Редактировать
            </button>
            <button
              className="mt-btn mt-btn--sm mt-btn--ghost"
              onClick={() => navigator.clipboard?.writeText(target)}
              type="button"
              title="Скопировать перевод"
            >
              Копировать
            </button>
          </div>
          <div
            className="te-pane__body"
            data-mt-pane-side="target"
            onMouseOver={
              targetMode === "preview" ? handlePaneMouseOver : undefined
            }
            onMouseLeave={
              targetMode === "preview" ? handlePaneMouseLeave : undefined
            }
          >
            {targetMode === "preview" ? (
              <p>
                {renderHighlighted(target || draft.target_text || "", {
                  pane: "target",
                  constraints,
                  enforcementEvents,
                  acceptedVariations,
                  highRiskFragments: targetRisk?.high_risk_fragments,
                  highlightAbbreviations: true,
                  activeLinkIds,
                })}
              </p>
            ) : (
              <textarea
                className="te-pane__editable"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                spellCheck={false}
                disabled={draftIsTerminal}
                placeholder="Перевод появится после translate…"
                autoFocus
              />
            )}
          </div>

          {/* FAILURES INLINE — only if any failed */}
          {failedCount > 0 && (
            <div className="te-failures">
              <div className="te-failures__title">
                <span className="mt-sev mt-sev--high">Высокий</span>
                <span>
                  {failedCount} {ruCheckPlural(failedCount)} не пройден{failedCount === 1 ? "а" : "о"}
                </span>
                <span className="mt-toolbar__sep" style={{ flex: 1 }} />
                {enforcementCount > 0 && (
                  <span
                    className="mt-text-tight mt-muted"
                    style={{
                      fontWeight: "var(--fw-regular)",
                      textTransform: "none",
                      letterSpacing: 0,
                    }}
                  >
                    {enforcementCount} автоматическ{enforcementCount === 1 ? "ая правка применена" : "их правок применено"}
                  </span>
                )}
              </div>
              <div className="te-failures__list">
                {failedChecks.slice(0, 5).map((check, idx) => {
                  const c = check as Record<string, unknown>;
                  return (
                    <div className="te-failures__item" key={`fc-${idx}`}>
                      <span className="te-failures__check">
                        {String(c.check_id ?? "check")}
                      </span>
                      <span>
                        {String(c.review_hint ?? c.message ?? "(нет описания)")}
                      </span>
                      <span className="mt-badge mt-badge--neutral mt-badge--no-dot">
                        {String(c.severity ?? "—")}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* RIGHT RAIL */}
        <aside
          className="te-rail"
          onMouseOver={handlePaneMouseOver}
          onMouseLeave={handlePaneMouseLeave}
        >
          <ConstraintsSection
            constraints={constraints}
            target={target}
            activeLinkId={activeLinkId}
          />
          <RiskSection
            risk={risk}
            activeLinkId={activeLinkId}
          />
          {targetRisk && (targetRisk.high_risk_fragments?.length ?? 0) > 0 && (
            <TargetRiskSection
              targetRisk={targetRisk}
              activeLinkId={activeLinkId}
            />
          )}
          <EnforcementSection
            events={enforcementEvents}
            activeLinkId={activeLinkId}
          />
          <DraftFeedbackPanel draftId={draftId} />
        </aside>
      </div>

      {/* FOOTER */}
      <footer className="te-footer">
        <span className="te-footer__brand">
          <span className="mt-topbar__mark" aria-hidden>m</span>
          medtranslate
        </span>
        <nav className="te-footer__links" aria-label="Юридическая информация">
          <a href="/terms" target="_blank" rel="noreferrer">
            Условия
          </a>
          <a href="/privacy" target="_blank" rel="noreferrer">
            Конфиденциальность
          </a>
          <a href="/security" target="_blank" rel="noreferrer">
            Безопасность данных
          </a>
        </nav>
        <span className="te-footer__sep" />
        <span className="te-footer__meta">
          Черновик · {draft.draft_id.slice(0, 8)} ·{" "}
          {snapshot.translation?.backend_name ?? "—"}
        </span>
      </footer>

      {/* Floating «Замечание» кнопка над выделением */}
      {showCommentButton && textSelection && (
        <button
          type="button"
          className="mt-btn mt-btn--primary mt-btn--sm"
          style={{
            position: "absolute",
            top:
              textSelection.rect.top +
              window.scrollY -
              36,
            left:
              textSelection.rect.left +
              window.scrollX +
              textSelection.rect.width / 2 -
              80,
            zIndex: 999,
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={openFeedbackPopover}
        >
          ✎ Замечание
        </button>
      )}

      {/* Сам popover */}
      {feedbackPopoverOpen && popoverSelection && (
        <FeedbackPopover
          draftId={draftId}
          selection={popoverSelection}
          pairedText={
            popoverSelection.side === "target"
              ? draft.source_text
              : target || draft.target_text
          }
          targetLang={(draft.target_lang as "ru" | "en") || "ru"}
          onClose={closeFeedbackPopover}
        />
      )}
      {/* Confirm-modal перед re-translate'ом, если target_text уже редактировался */}
      {confirmRetranslate && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.55)",
            display: "grid",
            placeItems: "center",
            zIndex: 1000,
          }}
          onClick={() => setConfirmRetranslate(null)}
        >
          <div
            className="cl-card"
            style={{
              maxWidth: 480,
              padding: "20px 24px",
              background: "var(--panel)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Перевести заново?</h3>
            <p style={{ color: "var(--muted-strong)", lineHeight: 1.5 }}>
              Текст перевода уже отредактирован вручную. Если запустить
              перевод через «{confirmRetranslate}», текущие правки будут
              <strong> потеряны без возможности отмены</strong>.
            </p>
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: 16,
              }}
            >
              <button
                type="button"
                className="mt-btn mt-btn--ghost"
                onClick={() => setConfirmRetranslate(null)}
              >
                Отмена
              </button>
              <button
                type="button"
                className="mt-btn mt-btn--primary"
                onClick={() => {
                  const target_backend = confirmRetranslate;
                  setConfirmRetranslate(null);
                  retranslateMutation.mutate(target_backend);
                }}
              >
                Перезаписать
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function DecisionBanner({
  decision,
}: {
  decision: NonNullable<SnapshotShape["decision_summary"]>;
}) {
  const tone = decision.tone || "review";
  return (
    <div className={`mt-decisionbanner mt-decisionbanner--${tone}`}>
      <div className="mt-decisionbanner__icon">{tone === "pass" ? "✓" : "!"}</div>
      <div>
        <div className="mt-decisionbanner__label">
          {decision.label || decision.status}
        </div>
        {decision.reason && (
          <div className="mt-decisionbanner__reason">{decision.reason}</div>
        )}
      </div>
    </div>
  );
}

function ConstraintsSection({
  constraints,
  target,
  activeLinkId,
}: {
  constraints: TerminologyConstraint[];
  target: string;
  activeLinkId: string | null;
}) {
  const missing = constraints.filter((c) => {
    if (!c.target_text) return false;
    const haystack = target.toLowerCase();
    // canonical в любой forme: target_text + accepted variants (там
    // pymorphy уже сложил inflected варианты) + target_abbreviation
    // для abbreviation-кейса. Если хоть один найден — constraint
    // считается выполненным.
    const acceptedForms: string[] = [
      c.target_text,
      ...(c.accepted_target_texts || []),
    ];
    const targetAbbrev = (c.provenance as { target_abbreviation?: string } | undefined)
      ?.target_abbreviation;
    if (targetAbbrev) acceptedForms.push(targetAbbrev);
    return !acceptedForms.some(
      (t) => t && haystack.includes(t.toLowerCase()),
    );
  });
  const activeRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (activeLinkId && activeRef.current) {
      activeRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [activeLinkId]);
  return (
    <div className="te-rail__sec">
      <div className="te-rail__head">
        <span className="te-rail__caret">▼</span>
        <span>Терминология</span>
        <span className="mt-badge mt-badge--neutral mt-badge--no-dot">
          {constraints.length}
        </span>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        {missing.length > 0 && (
          <span className="mt-badge mt-badge--danger mt-badge--no-dot">
            {missing.length} пропущено
          </span>
        )}
      </div>
      <div className="te-rail__body">
        {constraints.length === 0 ? (
          <div className="mt-empty">Нет обязательных терминов</div>
        ) : (
          <div className="mt-constraint-list">
            {constraints.map((c) => {
              const isMissing = missing.some(
                (m) => m.constraint_id === c.constraint_id,
              );
              const linkId = "c-" + c.constraint_id;
              const isActive = linkId === activeLinkId;
              const baseStyle: CSSProperties = {};
              if (isMissing) baseStyle.background = "var(--red-50)";
              if (isActive) {
                baseStyle.outline = "2px solid var(--blue-400)";
                baseStyle.outlineOffset = "1px";
              }
              return (
                <div
                  key={c.constraint_id}
                  ref={isActive ? activeRef : undefined}
                  data-mt-link={linkId}
                  className={
                    "mt-constraint" + (isActive ? " is-active" : "")
                  }
                  style={baseStyle}
                >
                  <div className="mt-constraint__pair">
                    <span className="mt-constraint__src">{c.source_text}</span>
                    <span className="mt-constraint__arrow">→</span>
                    <span
                      className="mt-constraint__tgt"
                      style={isMissing ? { color: "var(--red-700)" } : undefined}
                    >
                      {c.target_text}
                    </span>
                  </div>
                  <div className="mt-constraint__meta">
                    <span
                      className={`mt-badge mt-badge--solid mt-badge--no-dot ${badgeForPolicy(c.policy_action, c.resolution_mode, isMissing)}`}
                    >
                      {labelForPolicy(c.policy_action, c.resolution_mode, isMissing)}
                    </span>
                    {c.domain && <span>{c.domain}</span>}
                  </div>
                  {/* F9: альтернативные смыслы амбигуэнтных абревиатур.
                      provenance.alternatives заполняется resolver'ом при
                      ambiguous_default / soft_context — показываем их
                      прямо в sidebar, не только в popover'е. */}
                  {(() => {
                    const alts = (c.provenance?.alternatives ?? []).filter(
                      (a) => a.target_term,
                    );
                    if (alts.length === 0) return null;
                    return (
                      <div className="mt-constraint__alternatives">
                        <span className="mt-constraint__alt-hint">
                          ⚠ также возможно:
                        </span>
                        {alts.map((alt, idx) => (
                          <div className="mt-constraint__alt" key={`alt-${idx}`}>
                            <span className="mt-constraint__alt-target">
                              {alt.target_term}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/** F9: словарь reason_code → человечный label для sidebar'а. Если код
 *  не в словаре, fallback на сам код (для future codes). */
const REASON_LABELS: Record<string, string> = {
  source_safety_signal: "сигнал опасности в источнике",
  dose_or_measurement: "доза или измерение",
  ambiguous_resolution: "амбигуэнтное разрешение",
  abbreviation_requires_review: "требует ревью аббревиатуры",
  blocked_source_term: "запрещённая форма в источнике",
  runtime_critical_term: "критичный термин (runtime)",
  "high_risk_domain:Drug": "high-risk: препарат",
  "high_risk_domain:DrugSubstance": "high-risk: фарм-вещество",
  "high_risk_domain:DrugClass": "high-risk: класс препаратов",
  "high_risk_domain:Disease": "high-risk: заболевание",
  "high_risk_domain:Regulatory": "high-risk: регуляторика",
  "high_risk_domain:LabMeasure": "high-risk: лабораторное измерение",
  "high_risk_domain:MedicationUse": "high-risk: применение препарата",
};

function reasonLabel(code: string): string {
  return REASON_LABELS[code] ?? code;
}

function RiskSection({
  risk,
  activeLinkId,
}: {
  risk: RiskPayload | undefined;
  activeLinkId: string | null;
}) {
  if (!risk) return null;
  const high = risk.high_risk_terms.length + risk.high_risk_fragments.length;
  const attn = risk.attention_terms.length;
  return (
    <div className="te-rail__sec">
      <div className="te-rail__head">
        <span className="te-rail__caret">▼</span>
        <span>Сигналы риска</span>
        {high > 0 && (
          <span className="mt-badge mt-badge--danger mt-badge--no-dot">
            {high} высок{high === 1 ? "ий" : "их"}
            {attn > 0 ? ` · ${attn} вним.` : ""}
          </span>
        )}
        {high === 0 && attn > 0 && (
          <span className="mt-badge mt-badge--warning mt-badge--no-dot">
            {attn} вним.
          </span>
        )}
      </div>
      <div className="te-rail__body">
        {high === 0 && attn === 0 ? (
          <div className="mt-empty">Нет рисков</div>
        ) : (
          <div className="mt-risk-list">
            {risk.high_risk_fragments.map((f, idx) => {
              const linkId = "q-" + quantityNorm(f.fragment);
              const isActive = linkId === activeLinkId;
              return (
                <div
                  className={"mt-risk" + (isActive ? " is-active" : "")}
                  key={`f-${idx}`}
                  data-mt-link={linkId}
                  style={
                    isActive
                      ? { outline: "2px solid var(--red-400)", outlineOffset: "1px" }
                      : undefined
                  }
                >
                  <span className="mt-risk__surface">{f.fragment}</span>
                  <div>
                    <div>{f.description ?? "высокорисковый фрагмент"}</div>
                    <div className="mt-risk__codes">
                      {f.reason_codes.map((rc) => (
                        <span key={rc}>{reasonLabel(rc)}</span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
            {risk.high_risk_terms.map((t, idx) => {
              const cid = (t as { constraint_id?: string }).constraint_id;
              const linkId = cid
                ? "c-" + cid
                : "t-" + t.surface.toLowerCase();
              const isActive = linkId === activeLinkId;
              return (
                <div
                  className={"mt-risk" + (isActive ? " is-active" : "")}
                  key={`t-${idx}`}
                  data-mt-link={linkId}
                  style={
                    isActive
                      ? { outline: "2px solid var(--red-400)", outlineOffset: "1px" }
                      : undefined
                  }
                >
                  <span className="mt-risk__surface">{t.surface}</span>
                  <div>
                    <div>{t.domain ?? "высокорисковый термин"}</div>
                    <div className="mt-risk__codes">
                      {t.reason_codes.map((rc) => (
                        <span key={rc}>{reasonLabel(rc)}</span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
            {risk.attention_terms.map((t, idx) => {
              const cid = (t as { constraint_id?: string }).constraint_id;
              const linkId = cid
                ? "c-" + cid
                : "t-" + t.surface.toLowerCase();
              const isActive = linkId === activeLinkId;
              return (
                <div
                  className={
                    "mt-risk mt-risk--attention" +
                    (isActive ? " is-active" : "")
                  }
                  key={`a-${idx}`}
                  data-mt-link={linkId}
                  style={
                    isActive
                      ? { outline: "2px solid var(--amber-400)", outlineOffset: "1px" }
                      : undefined
                  }
                >
                  <span className="mt-risk__surface">{t.surface}</span>
                  <div>
                    <div>{t.domain ?? "требует внимания"}</div>
                    <div className="mt-risk__codes">
                      {t.reason_codes.map((rc) => (
                        <span key={rc}>{reasonLabel(rc)}</span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/** F8: отдельная sidebar-секция под risk-фрагменты, найденные в TARGET
 *  тексте (после перевода). Это параллель к RiskSection (source-side).
 *  Не схлопывается с RiskSection чтобы было визуально видно где RU-сигнал,
 *  где EN-параллель. */
function TargetRiskSection({
  targetRisk,
  activeLinkId,
}: {
  targetRisk: NonNullable<SnapshotShape["target_risk"]>;
  activeLinkId: string | null;
}) {
  const fragments = targetRisk.high_risk_fragments ?? [];
  if (fragments.length === 0) return null;
  return (
    <div className="te-rail__sec">
      <div className="te-rail__head">
        <span className="te-rail__caret">▼</span>
        <span>Сигналы риска в переводе</span>
        <span className="mt-badge mt-badge--danger mt-badge--no-dot">
          {fragments.length} высок{fragments.length === 1 ? "ий" : "их"}
        </span>
      </div>
      <div className="te-rail__body">
        <div className="mt-risk-list">
          {fragments.map((f, idx) => {
            const linkId = "q-" + quantityNorm(f.fragment);
            const isActive = linkId === activeLinkId;
            return (
              <div
                className={"mt-risk" + (isActive ? " is-active" : "")}
                key={`tf-${idx}`}
                data-mt-link={linkId}
                style={
                  isActive
                    ? { outline: "2px solid var(--red-400)", outlineOffset: "1px" }
                    : undefined
                }
              >
                <span className="mt-risk__surface">{f.fragment}</span>
                <div>
                  <div>{f.description ?? "сигнал в переводе"}</div>
                  <div className="mt-risk__codes">
                    {(f.reason_codes ?? []).map((rc) => (
                      <span key={rc}>{reasonLabel(rc)}</span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function EnforcementSection({
  events,
  activeLinkId,
}: {
  events: EnforcementEvent[];
  activeLinkId: string | null;
}) {
  if (events.length === 0) {
    return (
      <div className="te-rail__sec">
        <div className="te-rail__head">
          <span className="te-rail__caret">▼</span>
          <span>Автоматические правки</span>
          <span className="mt-badge mt-badge--neutral mt-badge--no-dot">0</span>
        </div>
        <div className="te-rail__body">
          <div className="mt-empty">Нет автоматических исправлений</div>
        </div>
      </div>
    );
  }
  const applied = events.filter(
    (e) => !e.status || e.status === "applied",
  ).length;
  return (
    <div className="te-rail__sec">
      <div className="te-rail__head">
        <span className="te-rail__caret">▼</span>
        <span>Автоматические правки</span>
        <span className="mt-badge mt-badge--success mt-badge--no-dot">
          {applied} применено
        </span>
      </div>
      <div className="te-rail__body" style={{ padding: 0 }}>
        {events.slice(0, 20).map((event, idx) => {
          const action = event.action ?? "правка";
          const src = event.matched_alias ?? event.source_text ?? "";
          const tgt = event.applied_text ?? event.target_text ?? "";
          const linkId = event.constraint_id
            ? "c-" + event.constraint_id
            : undefined;
          const isActive = linkId === activeLinkId;
          return (
            <div
              className={"mt-enf" + (isActive ? " is-active" : "")}
              key={`enf-${idx}`}
              data-mt-link={linkId}
              style={
                isActive
                  ? { outline: "2px solid var(--teal-400)", outlineOffset: "1px" }
                  : undefined
              }
            >
              <span className="mt-enf__action">{action}</span>
              <div className="mt-enf__pair">
                {src && <span className="mt-enf__src">{src}</span>}
                {src && tgt && <span className="mt-enf__arrow">→</span>}
                {tgt && <span className="mt-enf__tgt">{tgt}</span>}
              </div>
              <span
                className={`mt-badge mt-badge--no-dot ${event.status === "applied" || !event.status ? "mt-badge--success" : "mt-badge--warning"}`}
              >
                {translateEventStatus(event.status)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers (RU plural / labels)
// ---------------------------------------------------------------------------

function countWords(text: string | null | undefined): number {
  if (!text) return 0;
  return (text.match(/\S+/g) || []).length;
}

function countMatched(
  target: string,
  constraints: TerminologyConstraint[],
): number {
  if (!target) return 0;
  const lc = target.toLowerCase();
  return constraints.reduce((acc, c) => {
    if (!c.target_text) return acc;
    const acceptedForms: string[] = [
      c.target_text,
      ...(c.accepted_target_texts || []),
    ];
    const targetAbbrev = (c.provenance as { target_abbreviation?: string } | undefined)
      ?.target_abbreviation;
    if (targetAbbrev) acceptedForms.push(targetAbbrev);
    const matched = acceptedForms.some(
      (t) => t && lc.includes(t.toLowerCase()),
    );
    return matched ? acc + 1 : acc;
  }, 0);
}

function badgeForPolicy(
  policyAction: string | undefined,
  resolutionMode: string | undefined,
  isMissing: boolean,
): string {
  if (isMissing) return "mt-badge--danger";
  if (policyAction === "blocked") return "mt-badge--danger";
  if (policyAction === "draft") return "mt-badge--warning";
  if (resolutionMode === "context_disambiguated") return "mt-badge--info";
  return "mt-badge--success";
}

function labelForPolicy(
  policyAction: string | undefined,
  resolutionMode: string | undefined,
  isMissing: boolean,
): string {
  if (isMissing) return "пропущено";
  if (policyAction === "blocked") return "заблокировано";
  // F9: убрали путающее «черновик» (конфликтовало со статусом черновика
  // самого draft'а). Различаем по resolution_mode:
  if (resolutionMode === "ambiguous_default") return "по умолчанию";
  if (resolutionMode === "soft_context") return "мягкое разрешение";
  if (policyAction === "draft") return "требует уточнения";
  if (resolutionMode === "context_disambiguated") return "по контексту";
  return "точно";
}

function statusLabelRu(status: string): string {
  switch (status) {
    case "open": return "открыт";
    case "saved": return "сохранён";
    case "sent_to_review": return "на ревью";
    case "released": return "выпущен";
    case "discarded": return "отменён";
    default: return status;
  }
}

function statusShortLabelRu(status: string): string {
  switch (status) {
    case "open":
    case "saved": return "черновик";
    case "sent_to_review": return "на ревью";
    case "released": return "выпущен";
    case "discarded": return "отменён";
    default: return "черновик";
  }
}

function translateEventStatus(status: string | null | undefined): string {
  switch (status) {
    case "applied": return "применено";
    case "skipped": return "пропущено";
    case "unresolved": return "не решено";
    default: return status ?? "применено";
  }
}

function ruPluralEnd(n: number): string {
  // термин / термина / терминов — для дока
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "а";
  return "ов";
}

function ruPluralEndShort(n: number): string {
  // сопоставлен / сопоставлено / сопоставлено
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "";
  return "о";
}

function ruWordPlural(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "слово";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "слова";
  return "слов";
}

function ruCheckPlural(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "проверка";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "проверки";
  return "проверок";
}
